import React from "react";
import {
  Collapse,
  Chip,
  IconButton,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  Stack,
  Avatar,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Tag as TagIcon,
} from "@mui/icons-material";
import { CheckStatus } from "../../../../../../../utils/enums";
import { CheckStatusChip } from "../../../../../../../components/CheckStatusChip/CheckStatusChip";

const ChecksListItem = ({
  check,
  index,
  isExpanded,
  onToggleExpand,
  checkToEvidences,
  handleDownloadDocument,
}) => {
  const isPassingCheck = check?.status === CheckStatus?.PASS;

  return (
    <React.Fragment>
      <ListItem
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: index % 2 === 0 ? "white" : "#f8fafc",
          p: 2,
          gap: 2,
        }}
      >
        <Chip
          label={`#${index + 1}`}
          size="small"
          sx={{
            height: 28,
            fontSize: "0.8rem",
            fontWeight: 700,
            backgroundColor: "#1976d2",
            color: "white",
            "& .MuiChip-label": {
              padding: "0 10px",
            },
          }}
        />

        <Box
          sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}
        >
          <Typography
            variant="body1"
            color="#1e293b"
            sx={{ fontWeight: 500, wordBreak: "break-word" }}
          >
            {check?.active_check_name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            ml: 2,
            flexShrink: 0,
          }}
        >
          {check?.status_updated_at && check?.status_updated_at !== "N/A" && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Typography
                variant="caption"
                color="#94a3b8"
                sx={{ whiteSpace: "nowrap" }}
              >
                {check?.status_updated_at}
              </Typography>
            </Box>
          )}

          <Tooltip title={check?.status_reason} arrow>
            <span>
              <CheckStatusChip status={check?.status} />
            </span>
          </Tooltip>

          {isPassingCheck ? (
            <IconButton
              size="small"
              onClick={() => onToggleExpand(check?.active_check_id)}
              sx={{ color: "primary.main" }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : (
            <IconButton size="small" disabled>
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Box>
      </ListItem>
      {isPassingCheck && (
        <Collapse in={isExpanded} timeout={300} unmountOnExit>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fafbfc",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Stack spacing={2}>
              {checkToEvidences[check?.active_check_id] &&
              checkToEvidences[check?.active_check_id]?.length > 0 ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#22c55e",
                        mr: 1.5,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#15803d"
                    >
                      Linked Evidences (
                      {checkToEvidences[check?.active_check_id]?.length})
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {checkToEvidences[check?.active_check_id]?.map(
                      (evidence, index) => (
                        <Box
                          key={evidence?.fileId}
                          sx={{
                            p: 1.5,
                            backgroundColor: "white",
                            borderRadius: 1,
                            border: "1px solid #e2e8f0",
                            borderLeft: "3px solid #22c55e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "#f0fdf4",
                              color: "#15803d",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}
                          >
                            #{index + 1}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              onClick={() =>
                                handleDownloadDocument(
                                  evidence?.path,
                                  evidence?.maskName
                                )
                              }
                              sx={{
                                fontWeight: 500,
                                color: "#1976d2",
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {evidence?.maskName}
                            </Typography>
                          </Box>
                          <Tooltip title="Evidence is valid and linked">
                            <CheckCircleIcon
                              sx={{ color: "#22c55e", fontSize: 20 }}
                            />
                          </Tooltip>
                        </Box>
                      )
                    )}
                  </Stack>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f8fafc",
                    textAlign: "center",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No evidence is required for this check to be completed.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Collapse>
      )}
    </React.Fragment>
  );
};

const ChecksSection = ({
  checks,
  expandedCheckId,
  onToggleExpand,
  checkToEvidences,
  handleDownloadDocument,
}) => (
  <Card
    sx={{
      borderRadius: 2,
      mb: 3,
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      border: "1px solid #e2e8f0",
    }}
    variant="outlined"
  >
    <Box
      sx={{
        borderBottom: "1px solid #ddd",
        padding: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "15px",
        backgroundColor: "#f8fafc",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <TagIcon sx={{ color: "#031B89" }} />
        <Typography variant="body1" fontWeight="bold">
          Checks
        </Typography>
        <Tooltip title="Checks are automatically checked against compliance requirements">
          <InfoIcon fontSize="small" sx={{ color: "#666", cursor: "help" }} />
        </Tooltip>
      </Box>
    </Box>
    <CardContent sx={{ padding: "unset", paddingBottom: "unset !important" }}>
      {checks?.length === 0 ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: "center", padding: "16px" }}
        >
          No Checks Found
        </Typography>
      ) : (
        <List sx={{ padding: 0, margin: 0 }}>
          {checks?.map((check, index) => (
            <ChecksListItem
              key={check?.active_check_id}
              check={check}
              index={index}
              isExpanded={expandedCheckId === check?.active_check_id}
              onToggleExpand={onToggleExpand}
              checkToEvidences={checkToEvidences}
              handleDownloadDocument={handleDownloadDocument}
            />
          ))}
        </List>
      )}
    </CardContent>
  </Card>
);

export default ChecksSection;
